import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, first } from 'rxjs/operators';
import { isEmailValid } from '@app/shared/utility';
import { FeatureDurationLoggerService } from '@app/shared/services/feature-duration-logger.service';
import { environment } from '@env/environment';
import { AuthService } from '@app/shared/services/auth.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { cssVariables } from '@env/css-vars';
import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import { ApplicationInsightEvent } from '@app/shared/enums';
import { Email, EmailService } from '@app/shared/services/api/email.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { lowerCase, trim } from 'lodash';
import { EmailContents } from '@app/shared/constants';
import { NullableString } from '@app/shared/models/nullable';
import { KamUser } from './models/kam-user';
import { ContactInquiryTypes } from './models/contact-inquiry-types';
import { MapStoreMunicipality } from '../shared/models/municipality/Municipality';

@Component({
  selector: 'app-contact-page',
  templateUrl: './contact-page.component.html',
  styleUrls: ['./contact-page.component.scss'],
})
export class ContactPageComponent implements OnInit, OnDestroy {
  public showFooterText = false;

  public contactTypes: ContactInquiryTypes[] = [];
  public content = '';
  public municipalityId = '';
  public selectedConcernType: NullableString = '';
  private kamUser: KamUser = {} as KamUser;
  private mapStoreServiceSubscription: Subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    public authService: AuthService,
    private appInsightsServiceDirective: AppInsightsServiceDirective,
    private emailService: EmailService,
    private matDialog: MatDialog,
    private overlayLoaderService: OverlayLoaderService,
    private subfeatureDurationLoggerService: FeatureDurationLoggerService,
    private translate: TranslateService,
    private userMunicipalitiesService: UserMunicipalitiesService
  ) {}

  public ngOnInit(): void {
    this.showFooterText = environment.tenant === 'edis';
    this.overlayLoaderService.isLoading = true;
    this.contactTypes = environment.contactInquiryTypes;
    this.subfeatureDurationLoggerService.initializeApplicationInsights('Kontakt');
    this.municipalityId = this.userMunicipalitiesService.currentMunicipality.guid;
    this.mapStoreServiceSubscription = this.userMunicipalitiesService.currentMunicipality$
      .pipe(
        filter((municipality: MapStoreMunicipality) => !!municipality?.guid && !!municipality?.key),
        distinctUntilChanged()
      )
      .subscribe((m: MapStoreMunicipality) => {
        this.municipalityId = m.guid;
      });

    this.overlayLoaderService.isLoading = false;
    this.setConcernTypeFromRoute();
  }

  public setKamUser(kam: KamUser): void {
    this.kamUser = kam;
    this.overlayLoaderService.isLoading = false;
  }

  public ngOnDestroy(): void {
    this.subfeatureDurationLoggerService.unsubscribbe();
    this.mapStoreServiceSubscription.unsubscribe();
  }

  public setType(value: ContactInquiryTypes): void {
    this.selectedConcernType = value.id;
  }

  public sendEmail(): void {
    if (!this.selectedConcernType || !this.content) {
      this.matDialog.open(NotificationDialogComponent, {
        data: {
          descriptionText: 'Bitte wählen Sie ein Thema aus.',
          descriptionFaClass: 'fa-exclamation-triangle fa-2x',
          descriptionFaColor: cssVariables['--ep-error-color'],
        },
        position: { top: '40px' },
        panelClass: ['-md'],
      });
      return;
    } else if (!isEmailValid(this.authService.currentUser.email.trim())) {
      this.matDialog.open(NotificationDialogComponent, {
        data: {
          descriptionText: this.translate.instant('module.contact.emailValidationMessage'),
          descriptionFaClass: 'fa-exclamation-triangle fa-2x',
          descriptionFaColor: cssVariables['--ep-error-color'],
        },
        position: { top: '40px' },
        panelClass: ['-md'],
      });
      return;
    }

    const concernType = this.findConcernType(this.selectedConcernType);
    const data = {
      KamName: `${this.kamUser.firstName} ${this.kamUser.lastName}`,
      Contactee: `${this.authService.currentUser.firstname} ${this.authService.currentUser.lastname}`,
      ReplyEmail: this.authService.currentUser.email.trim(),
      Content: this.content.replace(/\n/g, '<br>'),
      Concern: concernType?.name,
    };

    let recipientEmail = '';
    let ccEmail = '';

    if (lowerCase(trim(environment.tenant)) === 'ava') {
      recipientEmail = environment.inquiryEmail;
      ccEmail = this.kamUser.email;
    } else {
      recipientEmail = this.kamUser.email;
    }

    const email: Email = {
      subject: EmailContents.InquiryCreated.subjectFunc?.(concernType?.name) || '',
      templateId: EmailContents.InquiryCreated.templateId,
      recipient: recipientEmail,
      cc: ccEmail,
      data: JSON.stringify(data),
    };

    this.emailService.sendEmail(email, false).pipe(first()).subscribe();

    this.matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: 'Die Nachricht wurde versendet.',
        orientation: 'row',
        alignment: 'start',
      },
      panelClass: ['-md'],
    });

    this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.contactRequest, {
      ContactType: concernType,
      municipalityName: this.userMunicipalitiesService.currentMunicipality.name,
    });

    this.content = '';
    this.selectedConcernType = null;
  }

  public trackByFn(_: number, item: ContactInquiryTypes): string {
    return item.id;
  }

  private findConcernType(contactType: string): ContactInquiryTypes | null {
    return this.contactTypes.find((type) => type.name.toLocaleLowerCase().trim() === contactType || type.id.toLocaleLowerCase().trim() === contactType) ?? null;
  }

  private setConcernTypeFromRoute(): void {
    const contactType = this.route.snapshot.paramMap.get('contactTypes')?.trim().toLocaleLowerCase() ?? '';
    this.selectedConcernType = this.findConcernType(contactType)?.id ?? '';
  }
}
